import React from "react";
import { useGlobal } from "reactn";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import TrinketsContainer from "./TrinketsContainer";
import { useWowPath, getEncounterId } from "../../../hooks/path-hooks";
import {
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedNames,
  getIsHealer,
  getIsTank,
  PATCH,
  getFormattedEncounterType,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  EXPANSION,
} from "../../../lib/wowHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import NoData from "components/Common/NoData";
import Layout from "../Layout";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";

const TrinketsPageContainer = () => {
  const [windowBreakpoint] = useGlobal("responsive");
  const isMobile =
    windowBreakpoint === "MOBILE_SMALL" || windowBreakpoint === "MOBILE_MEDIUM" || windowBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: trinketsData, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_trinkets.json`,
    { ssr: true },
  );

  if (isLoading) {
    return null;
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    trinketsData &&
    (affix && affix !== "all"
      ? (trinketsData.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.trinkets?.total ?? 0) > 0
      : (trinketsData[encounterType]?.[encounterID]?.[dataClassSpec]?.trinkets?.total ?? 0) > 0);

  const encounterData =
    hasData &&
    (affix && affix !== "all"
      ? trinketsData.affixes[affix][encounterID][dataClassSpec].trinkets
      : trinketsData[encounterType][encounterID][dataClassSpec].trinkets);
  const trinkets = encounterData?.trinket_combos ?? [];
  const trinketPairs = encounterData?.trinket_combos2 ?? [];
  const totalTrinkets = encounterData?.total_trinkets ?? [];

  const isHealer = getIsHealer(displaySpecName);
  const isTank = getIsTank(displaySpecName);

  const pageDescription = `BIS trinkets for ${displaySpecName} ${displayClassName}. Get the best ${displaySpecName} ${displayClassName} trinkets, data-driven by top players in ${getFormattedEncounterType(
    encounterType,
  )}, for ${EXPANSION} ${PATCH}.`;

  return (
    <Layout
      className="wow-trinkets-container"
      classType={classType}
      filterPage="builds"
      metaTitle={`${displaySpecName} ${displayClassName} BIS Trinkets - ${EXPANSION} ${PATCH}`}
      pageDescription={pageDescription}
      pageTitle={`Trinkets for ${getFormattedEncounterType(encounterType)}`}
      specType={specType}
    >
      {hasData ? (
        <>
          <SubtitleAndDescription
            subtitle={`The Best ${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(
              displayClassName,
            )} Trinkets for ${getFormattedContentType(encounterType)}`}
            description={pageDescription}
          />
          <TrinketsContainer
            trinkets={trinkets}
            trinketPairs={trinketPairs}
            totalTrinkets={totalTrinkets}
            isMobile={isMobile}
            isHealer={isHealer}
            isTank={isTank}
            encounterType={encounterType}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best trinkets and other gear.`}
            guideData={[
              {
                description: `The best in-depth guides covering trinkets and gear for ${displaySpecName} ${displayClassName}`,
                link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                  classType,
                )}-pve-${getIcyVeinsRole(specType)}-gear-best-in-slot`,
                title: "Gear and Best in Slot Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default TrinketsPageContainer;
